
import { useState, useEffect, useRef } from "react";

const GOLD = "#C8A96E";
const IVORY = "#F2EDE4";
const DARK = "#0A0A08";
const DARK2 = "#141410";
const DARK3 = "#1E1E19";
const MUTED = "#7A7A6E";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  body {
    background: ${DARK};
    color: ${IVORY};
    font-family: 'DM Sans', sans-serif;
    font-weight: 300;
    overflow-x: hidden;
    cursor: none;
  }

  .cursor {
    position: fixed;
    pointer-events: none;
    z-index: 9999;
    mix-blend-mode: difference;
  }
  .cursor-dot {
    width: 8px; height: 8px;
    background: ${IVORY};
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: transform 0.1s;
  }
  .cursor-ring {
    width: 32px; height: 32px;
    border: 1px solid ${IVORY};
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: all 0.18s ease;
    opacity: 0.5;
  }

  .nav {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 100;
    padding: 28px 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: background 0.4s, padding 0.4s;
  }
  .nav.scrolled {
    background: rgba(10,10,8,0.92);
    backdrop-filter: blur(12px);
    padding: 18px 60px;
    border-bottom: 1px solid rgba(200,169,110,0.12);
  }
  .nav-logo {
    font-family: 'Cormorant Garamond', serif;
    font-size: 22px;
    font-weight: 400;
    letter-spacing: 0.12em;
    color: ${IVORY};
    text-transform: uppercase;
    cursor: pointer;
  }
  .nav-logo span { color: ${GOLD}; }
  .nav-links {
    display: flex;
    gap: 44px;
    list-style: none;
  }
  .nav-links a {
    color: ${MUTED};
    text-decoration: none;
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    transition: color 0.2s;
  }
  .nav-links a:hover { color: ${IVORY}; }
  .nav-cta {
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${DARK};
    background: ${GOLD};
    border: none;
    padding: 10px 24px;
    cursor: pointer;
    transition: opacity 0.2s;
    font-family: 'DM Sans', sans-serif;
    font-weight: 400;
  }
  .nav-cta:hover { opacity: 0.85; }

  section { position: relative; }

  /* HERO */
  .hero {
    min-height: 100vh;
    display: flex;
    align-items: flex-end;
    padding: 0 60px 80px;
    background: ${DARK};
    overflow: hidden;
  }
  .hero-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #0d0d0a 0%, #1a1810 50%, #0a0a08 100%);
  }
  .hero-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(200,169,110,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(200,169,110,0.04) 1px, transparent 1px);
    background-size: 80px 80px;
  }
  .hero-accent {
    position: absolute;
    top: 0; right: 0;
    width: 55%;
    height: 100%;
    overflow: hidden;
  }
  .hero-accent-inner {
    position: absolute;
    inset: 0;
    background: linear-gradient(160deg,
      rgba(200,169,110,0.08) 0%,
      rgba(200,169,110,0.03) 40%,
      transparent 70%
    );
  }
  .hero-lines {
    position: absolute;
    top: 60px; right: 60px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    opacity: 0.18;
  }
  .hero-lines div {
    height: 1px;
    background: ${GOLD};
    transform-origin: right;
    animation: expandLine 1.5s ease forwards;
  }
  @keyframes expandLine {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }

  .hero-content {
    position: relative;
    z-index: 1;
    max-width: 860px;
  }
  .hero-eyebrow {
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: ${GOLD};
    margin-bottom: 28px;
    display: flex;
    align-items: center;
    gap: 16px;
    opacity: 0;
    animation: fadeUp 0.8s ease 0.3s forwards;
  }
  .hero-eyebrow::before {
    content: '';
    width: 40px;
    height: 1px;
    background: ${GOLD};
    display: block;
  }
  .hero-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(52px, 7vw, 96px);
    font-weight: 300;
    line-height: 0.95;
    letter-spacing: -0.02em;
    color: ${IVORY};
    margin-bottom: 36px;
    opacity: 0;
    animation: fadeUp 0.9s ease 0.5s forwards;
  }
  .hero-title em {
    font-style: italic;
    color: ${GOLD};
  }
  .hero-sub {
    font-size: 15px;
    line-height: 1.7;
    color: ${MUTED};
    max-width: 460px;
    margin-bottom: 52px;
    opacity: 0;
    animation: fadeUp 0.9s ease 0.7s forwards;
  }
  .hero-actions {
    display: flex;
    align-items: center;
    gap: 36px;
    opacity: 0;
    animation: fadeUp 0.9s ease 0.9s forwards;
  }
  .btn-primary {
    background: ${GOLD};
    color: ${DARK};
    border: none;
    padding: 16px 40px;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.25s;
  }
  .btn-primary:hover { background: #d4b87c; transform: translateY(-1px); }
  .btn-ghost {
    background: transparent;
    color: ${IVORY};
    border: 1px solid rgba(242,237,228,0.2);
    padding: 16px 40px;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.25s;
  }
  .btn-ghost:hover { border-color: ${GOLD}; color: ${GOLD}; }
  .hero-scroll {
    position: absolute;
    bottom: 40px;
    right: 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    opacity: 0;
    animation: fadeUp 1s ease 1.2s forwards;
  }
  .hero-scroll span {
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${MUTED};
    writing-mode: vertical-lr;
  }
  .scroll-line {
    width: 1px;
    height: 60px;
    background: linear-gradient(to bottom, ${MUTED}, transparent);
    animation: scrollPulse 2s ease-in-out infinite;
  }
  @keyframes scrollPulse {
    0%, 100% { opacity: 0.3; transform: scaleY(0.8); }
    50% { opacity: 1; transform: scaleY(1); }
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* STATS BAR */
  .stats-bar {
    background: ${DARK3};
    border-top: 1px solid rgba(200,169,110,0.15);
    border-bottom: 1px solid rgba(200,169,110,0.15);
    padding: 32px 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .stat-item {
    text-align: center;
  }
  .stat-num {
    font-family: 'Cormorant Garamond', serif;
    font-size: 42px;
    font-weight: 300;
    color: ${GOLD};
    line-height: 1;
    letter-spacing: -0.02em;
  }
  .stat-label {
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${MUTED};
    margin-top: 6px;
  }
  .stat-divider {
    width: 1px;
    height: 40px;
    background: rgba(200,169,110,0.15);
  }

  /* SERVICES */
  .services {
    padding: 120px 60px;
    background: ${DARK};
  }
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 80px;
  }
  .section-eyebrow {
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: ${GOLD};
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .section-eyebrow::before {
    content: '';
    width: 30px;
    height: 1px;
    background: ${GOLD};
    display: block;
  }
  .section-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(36px, 4vw, 60px);
    font-weight: 300;
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: ${IVORY};
  }
  .section-title em { font-style: italic; color: ${GOLD}; }
  .section-desc {
    max-width: 360px;
    font-size: 14px;
    line-height: 1.8;
    color: ${MUTED};
    text-align: right;
  }

  .services-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2px;
  }
  .service-card {
    background: ${DARK2};
    border: 1px solid rgba(200,169,110,0.06);
    cursor: pointer;
    transition: all 0.35s;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .service-card::before {
    content: '';
    position: absolute;
    bottom: 0; left: 0;
    width: 100%;
    height: 2px;
    background: ${GOLD};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.35s ease;
    z-index: 2;
  }
  .service-card:hover { background: #161612; transform: translateY(-4px); }
  .service-card:hover::before { transform: scaleX(1); }
  .service-img-wrap {
    position: relative;
    width: 100%;
    height: 220px;
    overflow: hidden;
    flex-shrink: 0;
  }
  .service-img-wrap img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.6s ease;
    filter: brightness(0.75) saturate(0.7);
  }
  .service-card:hover .service-img-wrap img {
    transform: scale(1.06);
    filter: brightness(0.65) saturate(0.6);
  }
  .service-img-num {
    position: absolute;
    top: 20px;
    left: 24px;
    font-family: 'Cormorant Garamond', serif;
    font-size: 13px;
    color: ${GOLD};
    letter-spacing: 0.12em;
    z-index: 1;
    background: rgba(10,10,8,0.55);
    padding: 3px 10px;
    border: 1px solid rgba(200,169,110,0.25);
  }
  .service-content {
    padding: 36px 40px 52px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .service-num { display: none; }
  .service-icon { display: none; }
  .service-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 26px;
    font-weight: 400;
    color: ${IVORY};
    margin-bottom: 14px;
    line-height: 1.2;
  }
  .service-body {
    font-size: 13.5px;
    line-height: 1.85;
    color: ${MUTED};
    margin-bottom: 28px;
    flex: 1;
  }
  .service-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .service-tag {
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${GOLD};
    border: 1px solid rgba(200,169,110,0.25);
    padding: 5px 12px;
  }
  .service-arrow {
    position: absolute;
    bottom: 36px;
    right: 40px;
    width: 32px;
    height: 32px;
    border: 1px solid rgba(200,169,110,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${GOLD};
    transition: all 0.25s;
    z-index: 1;
  }
  .service-card:hover .service-arrow {
    background: ${GOLD};
    color: ${DARK};
    border-color: ${GOLD};
  }

  /* PROCESS */
  .process {
    padding: 120px 60px;
    background: ${DARK3};
    border-top: 1px solid rgba(200,169,110,0.08);
    border-bottom: 1px solid rgba(200,169,110,0.08);
  }
  .process-steps {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    margin-top: 80px;
  }
  .process-step {
    padding: 0 40px 0 0;
    position: relative;
  }
  .process-step:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 20px;
    right: 0;
    width: 1px;
    height: 60%;
    background: rgba(200,169,110,0.15);
  }
  .step-num {
    font-family: 'Cormorant Garamond', serif;
    font-size: 72px;
    font-weight: 300;
    color: rgba(200,169,110,0.12);
    line-height: 1;
    margin-bottom: 24px;
  }
  .step-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 22px;
    font-weight: 400;
    color: ${IVORY};
    margin-bottom: 14px;
  }
  .step-body {
    font-size: 13px;
    line-height: 1.85;
    color: ${MUTED};
  }

  /* ABOUT */
  .about {
    padding: 120px 60px;
    background: ${DARK};
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 100px;
    align-items: center;
  }
  .about-visual {
    position: relative;
  }
  .about-frame {
    background: ${DARK2};
    border: 1px solid rgba(200,169,110,0.1);
    padding: 56px 48px;
    position: relative;
  }
  .about-frame::before {
    content: '';
    position: absolute;
    top: -20px; left: -20px;
    right: 20px; bottom: 20px;
    border: 1px solid rgba(200,169,110,0.08);
    z-index: -1;
  }
  .about-quote {
    font-family: 'Cormorant Garamond', serif;
    font-size: 28px;
    font-weight: 300;
    font-style: italic;
    line-height: 1.5;
    color: ${IVORY};
    margin-bottom: 32px;
  }
  .about-quote span { color: ${GOLD}; }
  .about-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-top: 32px;
  }
  .about-metric {
    border-left: 2px solid ${GOLD};
    padding-left: 16px;
  }
  .about-metric-num {
    font-family: 'Cormorant Garamond', serif;
    font-size: 36px;
    font-weight: 300;
    color: ${GOLD};
    line-height: 1;
  }
  .about-metric-label {
    font-size: 12px;
    color: ${MUTED};
    margin-top: 4px;
    letter-spacing: 0.06em;
  }
  .about-text p {
    font-size: 14px;
    line-height: 1.9;
    color: ${MUTED};
    margin-bottom: 20px;
  }
  .about-text p strong {
    color: ${IVORY};
    font-weight: 400;
  }
  .team-badges {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 44px;
  }
  .team-badge {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    background: ${DARK2};
    border: 1px solid rgba(200,169,110,0.08);
  }
  .badge-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(200,169,110,0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Cormorant Garamond', serif;
    font-size: 16px;
    color: ${GOLD};
    flex-shrink: 0;
  }
  .badge-info { flex: 1; }
  .badge-name {
    font-size: 13px;
    color: ${IVORY};
    margin-bottom: 2px;
  }
  .badge-role {
    font-size: 11px;
    color: ${MUTED};
    letter-spacing: 0.06em;
  }
  .badge-exp {
    font-family: 'Cormorant Garamond', serif;
    font-size: 20px;
    color: ${GOLD};
    font-weight: 300;
  }

  /* PORTFOLIO TEASER */
  .portfolio {
    padding: 120px 60px;
    background: ${DARK};
    border-top: 1px solid rgba(200,169,110,0.08);
  }
  .portfolio-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 280px 280px;
    gap: 3px;
    margin-top: 60px;
  }
  .portfolio-item {
    position: relative;
    overflow: hidden;
    cursor: pointer;
    background: ${DARK2};
  }
  .portfolio-item:first-child {
    grid-row: 1 / 3;
    grid-column: 1;
  }
  .portfolio-bg {
    position: absolute;
    inset: 0;
    transition: transform 0.6s ease;
  }
  .portfolio-item:hover .portfolio-bg img { transform: scale(1.06); }
  .portfolio-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(10,10,8,0.75) 0%, rgba(10,10,8,0.15) 50%, transparent 100%);
    opacity: 0.5;
    transition: opacity 0.4s;
  }
  .portfolio-item:hover .portfolio-overlay { opacity: 1; }
  .portfolio-meta {
    position: absolute;
    bottom: 0; left: 0; right: 0;
    padding: 28px;
    transform: translateY(4px);
    opacity: 0.7;
    transition: all 0.4s;
  }
  .portfolio-item:hover .portfolio-meta { transform: translateY(0); opacity: 1; }
  .portfolio-cat {
    font-size: 10px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${GOLD};
    margin-bottom: 6px;
  }
  .portfolio-name {
    font-family: 'Cormorant Garamond', serif;
    font-size: 22px;
    font-weight: 300;
    color: ${IVORY};
  }
  .portfolio-pattern {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .portfolio-label {
    font-family: 'Cormorant Garamond', serif;
    font-size: 15px;
    color: rgba(200,169,110,0.3);
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  /* CLIENTS */
  .clients {
    padding: 80px 60px;
    background: ${DARK2};
    border-top: 1px solid rgba(200,169,110,0.08);
  }
  .clients-eyebrow {
    text-align: center;
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: ${MUTED};
    margin-bottom: 44px;
  }
  .clients-row {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 64px;
    flex-wrap: wrap;
  }
  .client-name {
    font-family: 'Cormorant Garamond', serif;
    font-size: 20px;
    font-weight: 300;
    color: rgba(242,237,228,0.2);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    transition: color 0.25s;
    cursor: default;
  }
  .client-name:hover { color: rgba(242,237,228,0.6); }

  /* CTA */
  .cta {
    padding: 140px 60px;
    background: ${DARK};
    border-top: 1px solid rgba(200,169,110,0.08);
    text-align: center;
    position: relative;
    overflow: hidden;
  }
  .cta-bg-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-family: 'Cormorant Garamond', serif;
    font-size: 280px;
    font-weight: 300;
    color: rgba(200,169,110,0.03);
    white-space: nowrap;
    pointer-events: none;
    letter-spacing: -0.04em;
  }
  .cta-content { position: relative; z-index: 1; }
  .cta-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(44px, 5.5vw, 80px);
    font-weight: 300;
    line-height: 1.05;
    color: ${IVORY};
    margin-bottom: 24px;
    letter-spacing: -0.02em;
  }
  .cta-title em { font-style: italic; color: ${GOLD}; }
  .cta-sub {
    font-size: 15px;
    color: ${MUTED};
    max-width: 460px;
    margin: 0 auto 52px;
    line-height: 1.7;
  }
  .cta-actions {
    display: flex;
    justify-content: center;
    gap: 20px;
    flex-wrap: wrap;
  }

  /* FOOTER */
  .footer {
    background: #060605;
    border-top: 1px solid rgba(200,169,110,0.1);
    padding: 60px 60px 40px;
  }
  .footer-top {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 60px;
    margin-bottom: 52px;
    padding-bottom: 52px;
    border-bottom: 1px solid rgba(200,169,110,0.06);
  }
  .footer-brand p {
    font-size: 13px;
    color: ${MUTED};
    line-height: 1.8;
    margin-top: 16px;
    max-width: 280px;
  }
  .footer-heading {
    font-size: 11px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${GOLD};
    margin-bottom: 20px;
  }
  .footer-links {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .footer-links a {
    font-size: 13px;
    color: ${MUTED};
    text-decoration: none;
    transition: color 0.2s;
  }
  .footer-links a:hover { color: ${IVORY}; }
  .footer-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .footer-copy {
    font-size: 12px;
    color: rgba(122,122,110,0.5);
    letter-spacing: 0.04em;
  }
  .footer-bottom-links {
    display: flex;
    gap: 28px;
    list-style: none;
  }
  .footer-bottom-links a {
    font-size: 12px;
    color: rgba(122,122,110,0.5);
    text-decoration: none;
    transition: color 0.2s;
  }
  .footer-bottom-links a:hover { color: ${MUTED}; }

  /* Contact Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(6,6,5,0.92);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(6px);
  }
  .modal {
    background: ${DARK2};
    border: 1px solid rgba(200,169,110,0.15);
    padding: 60px;
    max-width: 580px;
    width: 90%;
    position: relative;
  }
  .modal-close {
    position: absolute;
    top: 24px; right: 24px;
    background: none;
    border: 1px solid rgba(200,169,110,0.2);
    color: ${MUTED};
    width: 36px;
    height: 36px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    transition: all 0.2s;
  }
  .modal-close:hover { border-color: ${GOLD}; color: ${GOLD}; }
  .modal-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 40px;
    font-weight: 300;
    color: ${IVORY};
    margin-bottom: 8px;
    line-height: 1.1;
  }
  .modal-sub {
    font-size: 13px;
    color: ${MUTED};
    margin-bottom: 40px;
  }
  .form-group {
    margin-bottom: 20px;
  }
  .form-label {
    display: block;
    font-size: 10px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${MUTED};
    margin-bottom: 8px;
  }
  .form-input, .form-select, .form-textarea {
    width: 100%;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(200,169,110,0.15);
    color: ${IVORY};
    padding: 12px 16px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
  }
  .form-input:focus, .form-select:focus, .form-textarea:focus {
    border-color: rgba(200,169,110,0.5);
  }
  .form-textarea { min-height: 100px; resize: vertical; }
  .form-select option { background: ${DARK2}; }
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .form-submit {
    width: 100%;
    margin-top: 28px;
    background: ${GOLD};
    color: ${DARK};
    border: none;
    padding: 16px;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    cursor: pointer;
    transition: opacity 0.2s;
  }
  .form-submit:hover { opacity: 0.85; }

  /* MOBILE */
  @media (max-width: 900px) {
    .nav { padding: 20px 24px; }
    .nav.scrolled { padding: 14px 24px; }
    .nav-links, .nav-cta { display: none; }
    .hero { padding: 0 24px 60px; }
    .stats-bar { padding: 24px; gap: 0; flex-wrap: wrap; }
    .stat-divider { display: none; }
    .stat-item { width: 50%; padding: 12px 0; }
    .services { padding: 80px 24px; }
    .services-grid { grid-template-columns: 1fr; }
    .service-img-wrap { height: 200px; }
    .section-header { flex-direction: column; align-items: flex-start; gap: 20px; }
    .section-desc { text-align: left; }
    .process { padding: 80px 24px; }
    .process-steps { grid-template-columns: 1fr 1fr; }
    .about { padding: 80px 24px; grid-template-columns: 1fr; }
    .portfolio { padding: 80px 24px; }
    .portfolio-grid { grid-template-columns: 1fr 1fr; grid-template-rows: auto; }
    .portfolio-item:first-child { grid-row: auto; grid-column: auto; }
    .clients { padding: 60px 24px; }
    .clients-row { gap: 32px; }
    .cta { padding: 100px 24px; }
    .footer { padding: 40px 24px 28px; }
    .footer-top { grid-template-columns: 1fr 1fr; }
    .modal { padding: 40px 24px; }
    .form-row { grid-template-columns: 1fr; }
  }
`;

const serviceData = [
  {
    num: "01",
    title: "Architectural Rendering",
    body: "Photorealistic exterior and interior visualizations delivered as high-resolution stills or cinematic video walkthroughs. We translate your blueprints into compelling imagery that communicates design intent with absolute clarity.",
    tags: ["Exterior", "Interior", "4K Stills", "Video", "360° Tours"],
    img: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80&fit=crop",
  },
  {
    num: "02",
    title: "Real Estate Marketing",
    body: "End-to-end visual marketing packages for developers and realtors — from polished brochures to social-ready imagery. We craft the narrative that sells properties before groundbreaking.",
    tags: ["Brochures", "Marketing Decks", "Digital Assets", "Print"],
    img: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80&fit=crop",
  },
  {
    num: "03",
    title: "AI Concept Development",
    body: "Accelerate design exploration using our AI-enhanced workflow. We partner with architects and designers to rapidly iterate concepts — generating multiple spatial directions in days rather than weeks.",
    tags: ["AI Rendering", "Concept Sketches", "Design Iteration", "Rapid Prototyping"],
    img: "https://images.unsplash.com/photo-1545989253-02cc26577f88?w=800&q=80&fit=crop",
  },
];

const processSteps = [
  { num: "01", title: "Discovery & Brief", body: "We begin with a thorough design review — studying your drawings, understanding project goals, target audience, and the atmosphere you want to convey." },
  { num: "02", title: "Art Direction", body: "Our team establishes camera angles, lighting scenarios, material palettes, and mood references before any production begins." },
  { num: "03", title: "Production", body: "Our studio produces all assets using industry-leading 3D software. You receive progress previews and can request focused refinements." },
  { num: "04", title: "Delivery", body: "Final outputs arrive in print and digital-ready formats. Video content is delivered graded and ready for marketing deployment." },
];

const portfolioItems = [
  {
    cat: "Exterior Rendering",
    name: "Meridian Tower, DC",
    img: "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=900&q=80&fit=crop",
  },
  {
    cat: "Interior Visualization",
    name: "The Sterling Residences",
    img: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&q=80&fit=crop",
  },
  {
    cat: "Real Estate Marketing",
    name: "Harbor Point Development",
    img: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80&fit=crop",
  },
  {
    cat: "AI Concept Series",
    name: "Cascade Cultural Center",
    img: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=800&q=80&fit=crop",
  },
  {
    cat: "Cinematic Walkthrough",
    name: "Ellipse Business Park",
    img: "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=800&q=80&fit=crop",
  },
];

const teamMembers = [
  { initials: "AR", role: "Senior Visualization Director", spec: "Exterior & Master Planning", years: "18+" },
  { initials: "AR", role: "Lead Architectural Designer", spec: "Interior & Residential", years: "15+" },
  { initials: "AR", role: "3D & AI Rendering Specialist", spec: "Cinematic & Marketing", years: "12+" },
];

export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const [modal, setModal] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", service: "", message: "" });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onMove = (e) => setCursorPos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const heroLines = [200, 160, 120, 90, 64, 44];

  return (
    <>
      <style>{styles}</style>

      {/* Custom cursor */}
      <div className="cursor" style={{ left: cursorPos.x, top: cursorPos.y }}>
        <div className="cursor-dot" />
      </div>
      <div className="cursor cursor-ring" style={{ left: cursorPos.x, top: cursorPos.y }} />

      {/* Navigation */}
      <nav className={`nav ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          Phoenix<span>.</span>Studio
        </div>
        <ul className="nav-links">
          {["Services", "Process", "About", "Portfolio"].map((l) => (
            <li key={l}>
              <a href="#" onClick={(e) => { e.preventDefault(); scrollTo(l.toLowerCase()); }}>
                {l}
              </a>
            </li>
          ))}
        </ul>
        <button className="nav-cta" onClick={() => setModal(true)}>
          Start a Project
        </button>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-grid" />
        <div className="hero-accent">
          <div className="hero-accent-inner" />
        </div>
        <div className="hero-lines">
          {heroLines.map((w, i) => (
            <div key={i} style={{ width: w, animationDelay: `${i * 0.12}s` }} />
          ))}
        </div>

        <div className="hero-content">
          <div className="hero-eyebrow">Architectural Visualization Studio</div>
          <h1 className="hero-title">
            Where Architecture<br />
            Becomes <em>Visible</em>
          </h1>
          <p className="hero-sub">
            Photorealistic renderings, cinematic walkthroughs, and AI-accelerated design exploration — crafted by architects with 15+ years of industry experience.
          </p>
          <div className="hero-actions">
            <button className="btn-primary" onClick={() => setModal(true)}>
              Start a Project
            </button>
            <button className="btn-ghost" onClick={() => scrollTo("portfolio")}>
              View Our Work
            </button>
          </div>
        </div>

        <div className="hero-scroll">
          <div className="scroll-line" />
          <span>Scroll</span>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="stats-bar">
        {[
          { num: "15+", label: "Years of Experience" },
          { num: "400+", label: "Projects Delivered" },
          { num: "3", label: "Core Services" },
          { num: "24h", label: "Concept Turnaround" },
          { num: "100%", label: "Client Retention Rate" },
        ].map((s, i) => (
          <>
            {i > 0 && <div className="stat-divider" key={`div-${i}`} />}
            <div className="stat-item" key={s.label}>
              <div className="stat-num">{s.num}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </>
        ))}
      </div>

      {/* Services */}
      <section className="services" id="services">
        <div className="section-header">
          <div>
            <div className="section-eyebrow">What We Do</div>
            <h2 className="section-title">
              Services Built<br />
              for <em>Architects</em>
            </h2>
          </div>
          <p className="section-desc">
            From concept sketch to final marketing asset — we handle every stage of the visual communication pipeline.
          </p>
        </div>
        <div className="services-grid">
          {serviceData.map((s) => (
            <div className="service-card" key={s.num} onClick={() => setModal(true)}>
              <div className="service-img-wrap">
                <img src={s.img} alt={s.title} />
                <div className="service-img-num">{s.num}</div>
              </div>
              <div className="service-content">
                <h3 className="service-title">{s.title}</h3>
                <p className="service-body">{s.body}</p>
                <div className="service-tags">
                  {s.tags.map((t) => <span className="service-tag" key={t}>{t}</span>)}
                </div>
              </div>
              <div className="service-arrow">→</div>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="process" id="process">
        <div className="section-header">
          <div>
            <div className="section-eyebrow">How We Work</div>
            <h2 className="section-title">
              A Studio<br />
              <em>Process</em> That Delivers
            </h2>
          </div>
          <p className="section-desc">
            Clarity at every stage — from first brief to final file delivery.
          </p>
        </div>
        <div className="process-steps">
          {processSteps.map((s) => (
            <div className="process-step" key={s.num}>
              <div className="step-num">{s.num}</div>
              <h3 className="step-title">{s.title}</h3>
              <p className="step-body">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About */}
      <section className="about" id="about">
        <div className="about-visual">
          <div className="about-frame">
            <p className="about-quote">
              "We don't just render buildings — we craft the <span>emotional experience</span> of spaces that don't yet exist."
            </p>
            <div className="about-grid">
              <div className="about-metric">
                <div className="about-metric-num">15+</div>
                <div className="about-metric-label">Years Architecture Experience</div>
              </div>
              <div className="about-metric">
                <div className="about-metric-num">3</div>
                <div className="about-metric-label">Senior Architects on Staff</div>
              </div>
              <div className="about-metric">
                <div className="about-metric-num">AI</div>
                <div className="about-metric-label">Enhanced Workflow</div>
              </div>
              <div className="about-metric">
                <div className="about-metric-num">DC</div>
                <div className="about-metric-label">Metro Area & Global Clients</div>
              </div>
            </div>
          </div>
        </div>
        <div className="about-text">
          <div className="section-eyebrow">About the Studio</div>
          <h2 className="section-title" style={{ marginBottom: 32 }}>
            Architects Who<br />
            <em>Visualize</em>
          </h2>
          <p>
            <strong>Phoenix Studio</strong> was founded by architects who grew tired of explaining designs through flat drawings. We understand building code, construction documents, and material science — because we've worked in architecture firms for over 15 years.
          </p>
          <p>
            Based in Northern Virginia, our team brings real architectural expertise to every frame. We've developed client-facing materials for national homebuilders, designed mobile AR experiences, and now combine that background with cutting-edge AI tools to deliver visualization that <strong>actually moves projects forward</strong>.
          </p>
          <p>
            Our AI-enhanced workflow doesn't replace architectural judgment — it amplifies it. We can generate a dozen spatial directions in a day, giving design teams genuine creative latitude without the production bottleneck.
          </p>

          <div className="team-badges">
            {teamMembers.map((m, i) => (
              <div className="team-badge" key={i}>
                <div className="badge-avatar">{m.initials}</div>
                <div className="badge-info">
                  <div className="badge-name">{m.role}</div>
                  <div className="badge-role">{m.spec}</div>
                </div>
                <div className="badge-exp">{m.years}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio */}
      <section className="portfolio" id="portfolio">
        <div className="section-header">
          <div>
            <div className="section-eyebrow">Selected Work</div>
            <h2 className="section-title">
              Projects That<br />
              <em>Speak</em>
            </h2>
          </div>
          <p className="section-desc">
            A selection of exterior renderings, interior visualizations, and marketing packages.
          </p>
        </div>
        <div className="portfolio-grid">
          {portfolioItems.map((item, i) => (
            <div className="portfolio-item" key={i} onClick={() => setModal(true)}>
              <div className="portfolio-bg">
                <img
                  src={item.img}
                  alt={item.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.6s ease" }}
                />
              </div>
              <div className="portfolio-overlay" />
              <div className="portfolio-meta">
                <div className="portfolio-cat">{item.cat}</div>
                <div className="portfolio-name">{item.name}</div>
              </div>
            </div>
          ))}
        </div>
      </section>



      {/* CTA */}
      <section className="cta">
        <div className="cta-bg-text">Phoenix</div>
        <div className="cta-content">
          <div className="section-eyebrow" style={{ justifyContent: "center", marginBottom: 20 }}>Let's Collaborate</div>
          <h2 className="cta-title">
            Your Vision.<br />
            Our <em>Expertise</em>.
          </h2>
          <p className="cta-sub">
            Whether you need a single hero render or a complete marketing package, we'd love to hear about your project.
          </p>
          <div className="cta-actions">
            <button className="btn-primary" onClick={() => setModal(true)}>Start a Project</button>
            <button className="btn-ghost" onClick={() => scrollTo("services")}>Explore Services</button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-top">
          <div className="footer-brand">
            <div className="nav-logo">Phoenix<span>.</span>Studio</div>
            <p>Architectural visualization crafted by architects. Serving developers, realtors, and design studios across the Mid-Atlantic and globally.</p>
          </div>
          <div>
            <div className="footer-heading">Services</div>
            <ul className="footer-links">
              {["Exterior Rendering", "Interior Visualization", "Video Walkthroughs", "Brochures & Marketing", "AI Concept Development"].map(l => (
                <li key={l}><a href="#" onClick={e => e.preventDefault()}>{l}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="footer-heading">Studio</div>
            <ul className="footer-links">
              {["About", "Process", "Portfolio", "Blog"].map(l => (
                <li key={l}><a href="#" onClick={e => e.preventDefault()}>{l}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="footer-heading">Contact</div>
            <ul className="footer-links">
              <li><a href="tel:7038997523">(703) 899-7523</a></li>
              <li><a href="mailto:gadamu@phoenixviz.com">gadamu@phoenixviz.com</a></li>
              <li style={{ color: MUTED, fontSize: 13, lineHeight: 1.6 }}>Lorton, Virginia<br />Serving Globally</li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-copy">© 2026 Phoenix Studio. All rights reserved.</div>
          <ul className="footer-bottom-links">
            <li><a href="#" onClick={e => e.preventDefault()}>Privacy</a></li>
            <li><a href="#" onClick={e => e.preventDefault()}>Terms</a></li>
          </ul>
        </div>
      </footer>

      {/* Contact Modal */}
      {modal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setModal(false); setSubmitted(false); } }}>
          <div className="modal">
            <button className="modal-close" onClick={() => { setModal(false); setSubmitted(false); }}>✕</button>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 16, color: GOLD, fontFamily: "'Cormorant Garamond', serif" }}>✓</div>
                <h2 className="modal-title" style={{ fontSize: 32, marginBottom: 12 }}>Message Received</h2>
                <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.7 }}>
                  Thank you for reaching out. A member of our team will be in touch within 24 hours.
                </p>
                <button className="btn-primary" style={{ marginTop: 32 }} onClick={() => { setModal(false); setSubmitted(false); }}>
                  Close
                </button>
              </div>
            ) : (
              <>
                <h2 className="modal-title">Start a Project</h2>
                <p className="modal-sub">Tell us about your vision — we'll respond within 24 hours.</p>
                <form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Your Name</label>
                      <input className="form-input" required placeholder="Jane Smith" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email Address</label>
                      <input className="form-input" type="email" required placeholder="jane@firm.com" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Service Interest</label>
                    <select className="form-select" value={formData.service} onChange={e => setFormData({ ...formData, service: e.target.value })}>
                      <option value="">Select a service...</option>
                      <option>Architectural Rendering (Image)</option>
                      <option>Architectural Rendering (Video)</option>
                      <option>Real Estate Marketing Package</option>
                      <option>AI Concept Development</option>
                      <option>Multiple Services</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Project Brief</label>
                    <textarea className="form-textarea" placeholder="Describe your project, timeline, and any specific requirements..." value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} />
                  </div>
                  <button type="submit" className="form-submit">Send Project Brief →</button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
